// How a verbatim quote is shown: whole, or cut at a word boundary with an ellipsis, and marked
// as an excerpt when it starts mid-sentence or stops short of a sentence end. The text itself is
// never altered; only the ellipses around it are added.
//
//   "…will no longer permit threats to America…"     excerpt on both sides
//   "We will measure our success not only by […]"     cut for space; a control shows the rest

const HIDDEN_MIN = 20;
const SENTENCE_END = /[.!?…]["'”’)\]]*$/;
const TRAILING_PUNCT = /[\s,;:–—-]+$/;
const STARTS_LOWER = /^[a-z]/;
const WHITESPACE = /\s/;
const ELLIPSIS = '…';

export interface QuoteView {
  /** '…' when the quote starts mid-sentence. */
  lead: '…' | '';
  text: string;
  /** '…' when the text stops before the end of a sentence. */
  tail: '…' | '';
  /** True when `text` is shorter than the quote, so the full text can be revealed. */
  hidden: boolean;
}

/** End of the last whole word within `limit` characters; a single longer word is kept whole. */
function wordEnd(text: string, limit: number): number {
  if (WHITESPACE.test(text.charAt(limit))) {
    return limit;
  }

  for (let i = limit - 1; i > 0; i--) {
    if (WHITESPACE.test(text.charAt(i))) {
      return i;
    }
  }

  const next = text.slice(limit).search(WHITESPACE);
  return next === -1 ? text.length : limit + next;
}

/** The quote as it is shown at `limit` characters, or in full for `'full'`. */
export function quoteView(quote: string, limit: number | 'full'): QuoteView {
  const whole = quote.trim();
  const lead = STARTS_LOWER.test(whole) ? ELLIPSIS : '';
  const closed = SENTENCE_END.test(whole);
  const fullView: QuoteView = { lead, text: whole, tail: closed ? '' : ELLIPSIS, hidden: false };
  if (limit === 'full' || whole.length <= limit) {
    return fullView;
  }

  const text = whole.slice(0, wordEnd(whole, limit)).replace(TRAILING_PUNCT, '');
  if (whole.length - text.length < HIDDEN_MIN) {
    return fullView;
  }

  return { lead, text, tail: ELLIPSIS, hidden: true };
}
